import {Workspace} from '@innowise-group/core';
import {Elements} from 'react-flow-renderer';
import {WorkspaceElementData} from '../components/workspace-view';
import {updateWorkspaceData, UpdatedWorkspaceDataResult} from './update-workspace-data.utility';

type AbortWorkspacePayload = Omit<UpdatedWorkspaceDataResult, 'booked_status' | 'active_requests'>;

const normalizeId = (id: string | number | null | undefined): string => String(id ?? '');

const bookingFingerprint = (workspace: Workspace) =>
  (workspace.bookedStatus || []).map((booking) => ({
    employeeId: booking.employee?.id ?? null,
    start: booking.range?.startDay ?? null,
    end: booking.range?.endDay ?? null,
    comment: booking.comment ?? null,
  }));

// Statuses that only follow the desk's bookings: a booking made or cancelled turns one into the other.
const BOOKING_DRIVEN_STATUS: Record<string, string> = {Booked: 'Free', 'Coworking Booked': 'Coworking Free'};

const withoutBookings = (status: string): string => BOOKING_DRIVEN_STATUS[status] ?? status;

/**
 * Comparable snapshot of fields that Cancel must restore on the server. With `ignoreBookings` the bookings
 * and the status they drive are left out: Cancel must not touch bookings nobody changed in this session.
 */
export const getWorkspaceAbortFingerprint = (workspace: Workspace, ignoreBookings = false): string =>
  JSON.stringify({
    status: ignoreBookings ? withoutBookings(workspace.status) : workspace.status,
    employeeId: workspace.employee?.id ?? null,
    number: workspace.number ?? null,
    roomId: workspace.room?.id ?? null,
    rotateDegree: workspace.rotateDegree ?? 0,
    coordinates: workspace.coordinates ?? null,
    comment: workspace.comment ?? null,
    rangeStart: workspace.range?.startDay ?? null,
    rangeEnd: workspace.range?.endDay ?? null,
    booked: ignoreBookings ? [] : bookingFingerprint(workspace),
  });

/**
 * Wire payload for multiple-update. With `restoreBookings` a booked desk carries its first booking
 * (employee, dates, comment); without it the desk is sent without a booking, which the backend
 * reads as "leave the desk's bookings as they are" (HTLGT-2139 п. 9).
 */
export const toAbortUpdatePayload = (workspace: Workspace, restoreBookings = true): AbortWorkspacePayload => {
  const {
    booked_status: _booked,
    active_requests: _active,
    ...payload
  } = updateWorkspaceData(workspace, restoreBookings);
  return payload;
};

export interface BuildAbortWorkspaceUpdatesArgs {
  initialWorkspaces: Elements<WorkspaceElementData>;
  currentWorkspaces: Elements<WorkspaceElementData>;
  workspacesToRevert?: Workspace[];
  /** Desks whose booking the user changed in this edit session: only they get their bookings back. */
  bookingEditedIds?: ReadonlySet<string>;
}

/**
 * Only desks that differ from the edit-session baseline (plus desks of other plans from the revert list).
 * Avoids PATCHing the entire floor on Cancel.
 */
export const buildAbortWorkspaceUpdates = ({
  initialWorkspaces,
  currentWorkspaces,
  workspacesToRevert = [],
  bookingEditedIds = new Set<string>(),
}: BuildAbortWorkspaceUpdatesArgs): AbortWorkspacePayload[] => {
  const currentById = new Map(
    currentWorkspaces
      .filter((element) => !element.data?.isDraft)
      .map((element) => [normalizeId(element.data.id), element.data]),
  );
  const baselineIds = new Set(initialWorkspaces.map((element) => normalizeId(element.data?.id)));

  const updatesById = new Map<string, AbortWorkspacePayload>();

  initialWorkspaces.forEach((element) => {
    const initial = element.data;
    if (!initial?.id) {
      return;
    }
    const id = normalizeId(initial.id);
    const current = currentById.get(id);
    // A desk that left the plan (removed together with its room) is not restored.
    if (!current) {
      return;
    }
    const bookingEdited = bookingEditedIds.has(id);
    if (
      getWorkspaceAbortFingerprint(initial, !bookingEdited) === getWorkspaceAbortFingerprint(current, !bookingEdited)
    ) {
      return;
    }
    const payload = toAbortUpdatePayload(initial, bookingEdited);
    if (!bookingEdited && withoutBookings(initial.status) === withoutBookings(current.status)) {
      // Bookings changed by others (or not at all): keep the status they give the desk now (HTLGT-2139 п. 9).
      payload.status = current.status;
    }
    updatesById.set(id, payload);
  });

  workspacesToRevert.forEach((workspace) => {
    const id = normalizeId(workspace?.id);
    // Desks of this plan, including the ones created in the session, are restored from the baseline;
    // the list covers only desks elsewhere, and the earliest snapshot of a desk wins.
    if (!workspace?.id || baselineIds.has(id) || currentById.has(id) || updatesById.has(id)) {
      return;
    }
    updatesById.set(id, toAbortUpdatePayload(workspace));
  });

  return Array.from(updatesById.values());
};
